import {InfiniteBrandSelector} from '@rdcomponents/brand-select/infinite-brand-selector';
import {Tabs} from 'antd';
import {Rule} from 'antd/lib/form';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {CompanyPopupV2} from '../../../layout/company-popup-v2/company-popup-v2';
import {Locale} from '../../../provider/locale/locale';
import {useLocale} from '../../../provider/locale/locale-hook';
import {
    PostSelectorCompanyType,
    UseCompaniesSelectorHookType,
} from '../../../service/feature-companies/feature-companies-type';
import {useFormRules} from '../../../service/form/form-rules-hook';
import {Form} from '../../../typings/antd';
import {getEnumValueEnsure} from '../../../util/enum';

import {
    PublicationCompaniesSourceEnum,
    PublicationCompaniesSourceFieldsEnum,
    PublicationCompaniesSourceFormType,
} from './publication-brand-or-companies-helper';
import {PublicationSelectedCompaniesInput} from './publication-selected-companies-input';
import * as styles from './publication-brand-or-companies-field.scss';

type PropsType = {
    companiesSelector: UseCompaniesSelectorHookType<PostSelectorCompanyType>;
    source: PublicationCompaniesSourceEnum;
    onSourceChange: (source: PublicationCompaniesSourceEnum) => void;
    disabled?: boolean;
};

// Single-step replacement for the "select companies / all brand companies" wizard steps: one card
// with a Brand tab (pick a brand, its companies are resolved on submit) and a Companies tab (the
// standard companies selector popup). Lives here because the promotions form moves onto it next.
export function PublicationBrandOrCompaniesField(props: PropsType): JSX.Element {
    const {companiesSelector, source, onSourceChange, disabled} = props;

    const form = Form.useFormInstance<PublicationCompaniesSourceFormType>();
    const {getLocalizedString} = useLocale();
    const {requiredFieldRule} = useFormRules();

    const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);

    const {selectedCount, initialCompaniesCountLoaded, selectorId, isInProgress} = companiesSelector;

    // The selected companies live in the selector, not in the form, so their count is mirrored into
    // a form field to make it validatable and watchable like any other required field. The selector
    // owns the value outright — seeding it from the edited publication would be pointless, since the
    // mirror overwrites it on mount and reports 0 until the selector has loaded its companies. That
    // is also what keeps submitting blocked until they are there: the API resolves the companies from
    // `selectorId`, so an early submit would save the publication with none.
    useEffect(() => {
        form.setFieldValue(PublicationCompaniesSourceFieldsEnum.CompaniesCount, selectedCount);

        if (selectedCount > 0) {
            form.setFields([{name: PublicationCompaniesSourceFieldsEnum.CompaniesCount, errors: []}]);
        }
    }, [form, selectedCount]);

    const selectedCompaniesRule = useMemo<Rule>(
        () => ({
            validator: (_rule: unknown, value: number): Promise<void> =>
                value > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error(getLocalizedString('VALIDATION__ERROR__FIELD_IS_REQUIRED'))),
        }),
        [getLocalizedString]
    );

    const handleSourceChange = useCallback(
        (activeKey: string) => {
            const nextSource = getEnumValueEnsure(
                PublicationCompaniesSourceEnum,
                activeKey,
                PublicationCompaniesSourceEnum.Brand
            );

            if (nextSource === PublicationCompaniesSourceEnum.Companies) {
                form.setFieldValue(PublicationCompaniesSourceFieldsEnum.BrandId, null);
            }

            onSourceChange(nextSource);
        },
        [form, onSourceChange]
    );

    return (
        <>
            <Form.Item label={<Locale stringKey="PUBLICATION_FORM__BRAND_OR_COMPANIES__LABEL" />} required>
                <div className={styles.PublicationBrandOrCompaniesField_card}>
                    <Tabs
                        activeKey={source}
                        className={styles.PublicationBrandOrCompaniesField_tabs}
                        destroyInactiveTabPane
                        items={[
                            {
                                key: PublicationCompaniesSourceEnum.Brand,
                                label: <Locale stringKey="PUBLICATION_FORM__BRAND_OR_COMPANIES__BRAND" />,
                                children: (
                                    <Form.Item<PublicationCompaniesSourceFormType>
                                        className={styles.PublicationBrandOrCompaniesField_item}
                                        name={PublicationCompaniesSourceFieldsEnum.BrandId}
                                        rules={[requiredFieldRule]}
                                    >
                                        <InfiniteBrandSelector
                                            className={styles.PublicationBrandOrCompaniesField_brandSelect}
                                            disabled={disabled}
                                            placeholder={getLocalizedString(
                                                'PUBLICATION_FORM__BRAND_OR_COMPANIES__BRAND_PLACEHOLDER'
                                            )}
                                        />
                                    </Form.Item>
                                ),
                            },
                            {
                                key: PublicationCompaniesSourceEnum.Companies,
                                label: <Locale stringKey="PUBLICATION_FORM__BRAND_OR_COMPANIES__COMPANIES" />,
                                children: (
                                    <Form.Item<PublicationCompaniesSourceFormType>
                                        className={styles.PublicationBrandOrCompaniesField_item}
                                        name={PublicationCompaniesSourceFieldsEnum.CompaniesCount}
                                        rules={[selectedCompaniesRule]}
                                    >
                                        <PublicationSelectedCompaniesInput
                                            disabled={disabled || !selectorId}
                                            isCountLoaded={initialCompaniesCountLoaded}
                                            onSelect={() => setIsPopupOpen(true)}
                                        />
                                    </Form.Item>
                                ),
                            },
                        ]}
                        onChange={handleSourceChange}
                    />
                </div>
            </Form.Item>

            <CompanyPopupV2<PostSelectorCompanyType>
                companiesSelector={companiesSelector}
                disabled={isInProgress}
                isOpen={isPopupOpen}
                noClearAfterClose
                onCloseModal={() => setIsPopupOpen(false)}
                onOk={() => setIsPopupOpen(false)}
            />
        </>
    );
}
